import { useState, useEffect, useCallback } from 'react';

interface PendingApi {
  id: string;
  url: string;
  method: string;
  startTime: number;
  status: 'pending' | 'completed' | 'failed';
}

export const useApiTracker = () => {
  const [pendingApis, setPendingApis] = useState<Map<string, PendingApi>>(new Map());

  // Track when an API call starts
  const trackApiStart = useCallback((url: string, method: string = 'GET') => {
    const id = `${method}-${url}-${Date.now()}`;
    const apiCall: PendingApi = {
      id,
      url,
      method,
      startTime: Date.now(),
      status: 'pending'
    };
    
    setPendingApis(prev => new Map(prev).set(id, apiCall));
    
    return id;
  }, []);

  // Track when an API call completes
  const trackApiComplete = useCallback((id: string, status: 'completed' | 'failed' = 'completed') => {
    setPendingApis(prev => {
      const newMap = new Map(prev);
      const apiCall = newMap.get(id);
      if (apiCall) {
        apiCall.status = status;
        const duration = Date.now() - apiCall.startTime;
        newMap.delete(id); // Remove completed API
      }
      return newMap;
    });
  }, []);

  // Get count of pending APIs
  const getPendingCount = useCallback(() => {
    return pendingApis.size;
  }, [pendingApis]);

  // Get all pending APIs
  const getPendingApis = useCallback(() => {
    return Array.from(pendingApis.values());
  }, [pendingApis]);

  // Check if any APIs are pending
  const hasPendingApis = useCallback(() => {
    return pendingApis.size > 0;
  }, [pendingApis]);

  // Wait for all APIs to complete
  const waitForAllApis = useCallback(async (maxWaitTime: number = 60000) => {
    const startTime = Date.now();
    
    while (pendingApis.size > 0 && (Date.now() - startTime) < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    if (pendingApis.size > 0) {
      return false;
    }
    
    return true;
  }, [pendingApis]);

  return {
    trackApiStart,
    trackApiComplete,
    getPendingCount,
    getPendingApis,
    hasPendingApis,
    waitForAllApis,
    pendingApis: Array.from(pendingApis.values())
  };
};
